/**
 * MSIC Code Service - module-level cache loaded from msic_codes.
 */
import { supabase } from '../lib/supabase_client';

export interface MSICCode {
  code: string;
  description: string;
  malayDescription: string;
}

const PAGE_SIZE = 1000;

let cache: MSICCode[] | null = null;
let loadPromise: Promise<MSICCode[]> | null = null;

const mapRow = (row: { code?: string | null; description?: string | null; malay_description?: string | null }): MSICCode => ({
  code: row.code || '',
  description: row.description || '',
  malayDescription: row.malay_description || '',
});

const fetchAllMsicFromDb = async (): Promise<MSICCode[]> => {
  const all: MSICCode[] = [];
  let from = 0;

  while (true) {
    const { data, error } = await supabase
      .from('msic_codes')
      .select('code, description, malay_description')
      .order('code')
      .range(from, from + PAGE_SIZE - 1);

    if (error) {
      throw new Error(error.message || 'Failed to load MSIC codes from database');
    }

    const page = (data || []).map(mapRow).filter((item) => item.code);
    all.push(...page);

    if (!data || data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
  }

  if (all.length === 0) {
    throw new Error('MSIC codes are not loaded. Sync MSIC codes from the admin page first.');
  }

  return all;
};

export const parseMSICXml = (xml: string): MSICCode[] => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xml, 'text/xml');
  if (doc.querySelector('parsererror')) {
    throw new Error('MSIC.xml is not valid XML');
  }

  const items = Array.from(doc.querySelectorAll('item')).map((item) => ({
    code: item.getAttribute('MSICCode') || '',
    description: item.getAttribute('EnglishDescription') || '',
    malayDescription: item.getAttribute('BahasaMalayDescription') || '',
  })).filter((item) => item.code && item.description);

  if (items.length === 0) {
    throw new Error('No MSIC codes found in MSIC.xml');
  }

  return items;
};

export const loadMSICCodes = async (): Promise<MSICCode[]> => {
  if (cache) return cache;
  if (loadPromise) return loadPromise;

  loadPromise = fetchAllMsicFromDb()
    .then((rows) => {
      cache = rows;
      console.log(`✅ MSIC: Loaded ${cache.length} codes from database`);
      return cache;
    })
    .catch((err) => {
      loadPromise = null;
      throw err;
    });

  return loadPromise;
};

export const getMSICCodes = (): MSICCode[] => cache || [];

export const isMSICLoaded = (): boolean => cache !== null && cache.length > 0;

export const resetMSICCache = () => {
  cache = null;
  loadPromise = null;
};
